import { iconCircleMinus, iconCirclePlus } from "@kensho/icons";
import { useLogger } from "@kensho/lumberjack";
import {
  AlertDialog,
  Button,
  ButtonLink,
  Dialog,
  Icon,
  TextLink,
  Tooltip,
} from "@kensho/neo";
import { uniq } from "lodash-es";
import { use, useEffect, useState } from "react";
import { useParams } from "react-router";

import { EMAILS } from "../constants";
import { ExportLocalTranscriptButton } from "../core/transcription/actions/export-local-transcript-button";
import {
  VALID_AUDIO_FILE_EXTS,
  VALID_VIDEO_FILE_EXTS,
} from "../core/transcription/constants";
import { LoginPaths, useLoginURL } from "../hooks/use-login-url";
import { usePrevious } from "../hooks/use-previous";
import { UserContext } from "../providers/user-context";
import { ScribeError } from "../types/types";

interface ScribeErrorDisplay extends ScribeError {
  content: React.ReactNode;
  actions?: React.ReactNode;
  dismissable?: boolean;
}

interface ErrorDialogProps {
  isOpen: boolean;
  error?: ScribeError;
  onClose: () => void;
}

const faqLink = (
  <TextLink href="https://docs.kensho.com/scribe/v2/faq">FAQ Section</TextLink>
);

const emailLink = (
  <TextLink href={`mailto:${EMAILS.SUPPORT}`}>{EMAILS.SUPPORT}</TextLink>
);

function getErrorDisplay(
  onClose: () => void,
  loginPaths: LoginPaths,
  error?: ScribeError,
): ScribeErrorDisplay {
  switch (error?.type) {
    case "badRequest":
      return {
        ...error,
        title: "Bad Request",
        content: (
          <div className="flex flex-col gap-2">
            <p>There was a problem with the request.</p>
            <p>
              {error.detail ? (
                <>
                  <span className="font-bold">Reason: </span> {error?.detail}
                </>
              ) : (
                <>
                  Check the {faqLink} for additional troubleshooting
                  information.
                </>
              )}
            </p>
          </div>
        ),
      };

    case "multipleFiles":
      return {
        ...error,
        title: "Please Submit One File at a Time",
        content: (
          <>
            <p>
              The Scribe Interface only supports selecting one file upload at a
              time. If it is a priority for you to upload multiple files at
              once, we recommend using the Scribe Batch API or reaching out to{" "}
              {emailLink} to discuss how we can better meet your transcription
              needs.
            </p>
          </>
        ),
      };

    case "invalidFile":
      return {
        ...error,
        title: "Unsupported/Invalid File Type",
        content: (
          <>
            <p>
              Supported file types:{" "}
              {uniq([...VALID_AUDIO_FILE_EXTS, ...VALID_VIDEO_FILE_EXTS]).join(
                ", ",
              )}
            </p>
            <p className="mt-2">
              If you have specific file types you want us to support, please
              send feedback to {emailLink}.
            </p>
          </>
        ),
      };

    case "corruptedFile":
      return {
        ...error,
        title: "Unrecognizable or Corrupted File",
        content: (
          <>
            <p>
              This file is either corrupted or not supported by this browser. If
              you believe the file is not corrupted you can try converting it
              into another of our supported file formats (
              {uniq([...VALID_AUDIO_FILE_EXTS, ...VALID_VIDEO_FILE_EXTS]).join(
                ", ",
              )}
              ) and reuploading. If the error continues you can reach out to{" "}
              {emailLink}.
            </p>
          </>
        ),
      };

    case "unauthenticated":
      return {
        ...error,
        title: "Session Expired",
        content: (
          <p>
            You are unauthenticated or your session may have expired. Please{" "}
            <TextLink href={loginPaths.URL} forceServer>
              sign in
            </TextLink>{" "}
            or{" "}
            <TextLink href={loginPaths.MARKETPLACE_IDP_URL} forceServer>
              sign in with S&P Marketplace
            </TextLink>{" "}
            again.
          </p>
        ),
      };

    case "usageLimit":
      return {
        ...error,
        title: "You have exceeded your usage limit",
        content: (
          <>
            <p>
              You have exceeded your usage limit for transcribing. Please reach
              out to {emailLink} if you would like to increase or upgrade your
              plan.
            </p>
          </>
        ),
      };

    case "rateLimit":
      return {
        ...error,
        title: "You have exceeded your rate limit",
        content: (
          <>
            <p>
              You have exceeded the max amount of audio you can transcribe in a
              single hour. Try again later or see the {faqLink} for more
              information. If this continues or you think this message is in
              error, please contact {emailLink} for assistance.
            </p>
          </>
        ),
      };

    case "fileTooLarge":
      return {
        ...error,
        title: "File Too Large",
        content: (
          <>
            <p>
              Upload failed: file exceeds 1GB limit. Please try again with a
              smaller file or see the {faqLink} for more information. If this
              continues or you think this message is in error, please contact{" "}
              {emailLink} for assistance.
            </p>
          </>
        ),
      };

    case "transcriptNotFound":
      return {
        ...error,
        title: "Transcript Not Found",
        dismissable: false,
        content: (
          <div className="flex flex-col gap-2">
            <p>This transcript either doesn‘t exist or has expired.</p>
            <p>
              If you believe this is in error you can look for common
              troubleshooting measures in the {faqLink} or email {emailLink}.
            </p>
          </div>
        ),
        actions: (
          <ButtonLink href="/transcriptions">
            Go to my transcriptions
          </ButtonLink>
        ),
      };

    case "transcriptRequestTimedOut":
      return {
        ...error,
        title: "Request timed out",
        dismissable: false,
        content: (
          <>
            <p>
              Try again after refreshing the page. If the issue persists you can
              look for common troubleshooting measures in the {faqLink} or email{" "}
              {emailLink}.
            </p>
          </>
        ),
        actions: (
          // force a reload to clear state
          <ButtonLink
            forceServer
            href={window.location.pathname}
            intent="primary"
          >
            Reload page
          </ButtonLink>
        ),
      };

    case "saveTranscriptConflict":
      return {
        ...error,
        title: "Changes not saved",
        dismissable: false,
        content: (
          <p>
            Changes to the transcript could not be applied, a newer version is
            available. Please reload the page.
          </p>
        ),
        actions: (
          <>
            <ExportLocalTranscriptButton />
            {/* force a reload to clear state */}
            <ButtonLink
              forceServer
              href={window.location.pathname}
              intent="primary"
            >
              Reload page
            </ButtonLink>
          </>
        ),
      };

    case "saveTranscriptError":
      return {
        ...error,
        title: "Changes not saved",
        dismissable: false,
        content: <p>Could not save transcript. Please reload the page.</p>,
        actions: (
          <>
            <ExportLocalTranscriptButton />
            {/* force a reload to clear state */}
            <ButtonLink
              forceServer
              href={window.location.pathname}
              intent="primary"
            >
              Reload page
            </ButtonLink>
          </>
        ),
      };

    case "generic":
      return {
        ...error,
        content: (
          <>
            <p>
              The Scribe Interface has encountered an unknown error. Try again
              after refreshing the page. If the issue persists you can look for
              common troubleshooting measures in the {faqLink} or email{" "}
              {emailLink}.
            </p>
          </>
        ),
      };
    case "unknown":
    default:
      return {
        ...error,
        type: "unknown",
        title: "Unknown Error",
        content: (
          <>
            <p>
              The Scribe Interface has encountered an unknown error. Try again
              after refreshing the page. If the issue persists you can look for
              common troubleshooting measures in the {faqLink} or email{" "}
              {emailLink}.
            </p>
          </>
        ),
      };
  }
}

export function ErrorDialog(props: ErrorDialogProps) {
  const { isOpen, error, onClose } = props;
  const { transcriptId } = useParams();
  const prevError = usePrevious(error);
  const log = useLogger();
  const { user } = use(UserContext);
  const loginPaths = useLoginURL();
  const [errorDisplay, setErrorDisplay] = useState(
    getErrorDisplay(onClose, loginPaths, error),
  );
  const [showDetail, setShowDetail] = useState(true);

  useEffect(() => {
    if (error && error !== prevError) {
      log.error(`errorDialog:${error.type}:${error.status}:${error.title}`, {
        ...error,
        transcriptId,
        user: user?.email || "unauthenticated",
      });
    }
  }, [error, prevError, log, user, transcriptId]);

  useEffect(() => {
    // only update error when opened to prevent flash of default content during hide transition
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isOpen) setErrorDisplay(getErrorDisplay(onClose, loginPaths, error));
  }, [isOpen, error, onClose, loginPaths]);

  const dialogContent = (
    <section data-testid="error-dialog">
      <div className="mb-6">{errorDisplay.content}</div>
      {error?.detail && (
        <div className="mb-6">
          <div
            className="mr-px flex cursor-pointer items-center justify-between"
            onClick={() => setShowDetail((prev) => !prev)}
            onKeyDown={({ key }) =>
              key === "Enter" && setShowDetail((prev) => !prev)
            }
            role="button"
            tabIndex={0}
          >
            Error Detail
            {showDetail ? (
              <Tooltip content="Collapse">
                <Icon icon={iconCircleMinus} />
              </Tooltip>
            ) : (
              <Tooltip content="Expand">
                <Icon icon={iconCirclePlus} />
              </Tooltip>
            )}
          </div>
          {showDetail && (
            <div className="mt-4 max-h-40 overflow-auto bg-gray-200 p-3 font-mono text-xs">
              <p>User ID: {user?.email}</p>
              {transcriptId && <p>Transcript ID: {transcriptId}</p>}
              {error.status && <p>Status: {error.status}</p>}
              <p>Detail: {error.detail}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-4">
        {errorDisplay.actions && <>{errorDisplay.actions}</>}

        {!errorDisplay.actions && errorDisplay.dismissable !== false && (
          <Button onClick={onClose}>Dismiss</Button>
        )}
      </div>
    </section>
  );

  return errorDisplay.dismissable === false ? (
    <AlertDialog isOpen={isOpen} title={errorDisplay.title} backdrop="dark">
      {dialogContent}
    </AlertDialog>
  ) : (
    <Dialog
      isOpen={isOpen}
      title={errorDisplay.title}
      onClose={onClose}
      backdrop="dark"
    >
      {dialogContent}
    </Dialog>
  );
}
